import AsyncStorage from '@react-native-async-storage/async-storage';
import { clientLogger } from '@/lib/datadog';

// Time utilities for development mode overrides
export function getCurrentTime(): Date {
  const fixedVisualTime = process.env.EXPO_PUBLIC_PCKM_FIXED_TIME;
  if (__DEV__ && fixedVisualTime) {
    const parsed = new Date(fixedVisualTime);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }

  return new Date();
}

export async function setDevTimeOverride(dateTime: string | null): Promise<void> {
  try {
    if (dateTime) {
      await AsyncStorage.setItem('devTimeOverride', dateTime);
      clientLogger.info('Set dev time override', { dateTime });
    } else {
      await AsyncStorage.removeItem('devTimeOverride');
      clientLogger.info('Cleared dev time override');
    }
  } catch (error) {
    clientLogger.error('Failed to set dev time override', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

export async function getDevTimeOverride(): Promise<string | null> {
  try {
    return await AsyncStorage.getItem('devTimeOverride');
  } catch (error) {
    clientLogger.error('Failed to get dev time override', {
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

export async function getCurrentTimeWithOverride(): Promise<Date> {
  try {
    const devTimeOverride = await getDevTimeOverride();
    if (devTimeOverride) {
      const overrideDate = new Date(devTimeOverride);
      if (!isNaN(overrideDate.getTime())) {
        clientLogger.info('Using dev time override', { overrideDate: overrideDate.toISOString() });
        return overrideDate;
      }
    }
  } catch (error) {
    clientLogger.error('Failed to check time override', {
      error: error instanceof Error ? error.message : String(error),
    });
  }

  return new Date();
}

export function formatTimeRemaining(targetTime: Date, currentTime?: Date): { text: string; isPassed: boolean } {
  const now = currentTime || new Date();
  const timeDiff = targetTime.getTime() - now.getTime();

  if (timeDiff <= 0) {
    return { text: "Deadline passed", isPassed: true };
  }

  const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

  let text = "";
  if (days > 0) {
    text = `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours !== 1 ? 's' : ''}`;
  } else if (hours > 0) {
    text = `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
  } else {
    text = `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }

  return { text, isPassed: false };
}

// Development time presets for easy testing
export const DEV_TIME_PRESETS = {
  // Week 4 scenarios (using actual NFL Week 4 2025 dates)
  WEEK4_WEDNESDAY_MORNING: '2025-09-24T14:00:00.000Z', // 3 days before games
  WEEK4_FRIDAY_EVENING: '2025-09-26T22:00:00.000Z',    // 1 day before games
  WEEK4_SATURDAY_AFTERNOON: '2025-09-27T18:00:00.000Z', // Few hours before games
  WEEK4_SUNDAY_MORNING: '2025-09-28T16:00:00.000Z',     // Just before 1pm games
  WEEK4_SUNDAY_DURING_GAMES: '2025-09-28T20:00:00.000Z', // During games
  WEEK4_MONDAY_AFTER: '2025-09-29T14:00:00.000Z',      // After games completed
} as const;

export const DEV_TIME_PRESET_LABELS = {
  WEEK4_WEDNESDAY_MORNING: 'Week 4 - Wednesday Morning (3 days before)',
  WEEK4_FRIDAY_EVENING: 'Week 4 - Friday Evening (1 day before)',
  WEEK4_SATURDAY_AFTERNOON: 'Week 4 - Saturday Afternoon (few hours before)',
  WEEK4_SUNDAY_MORNING: 'Week 4 - Sunday Morning (just before games)',
  WEEK4_SUNDAY_DURING_GAMES: 'Week 4 - Sunday During Games',
  WEEK4_MONDAY_AFTER: 'Week 4 - Monday After (games completed)',
} as const;
